/**
 * «Cómo se calculó» una cantidad, para el CLIENTE que abre el enlace compartido (idea 9).
 *
 * Cada tipo de cantidad se explica en palabras de quien no hizo el presupuesto, sin la mecánica interna
 * (nada de nombres de capas, reglas del sistema ni «la IA eligió»):
 *   - plano         → se midió del plano: tabla de partes, con su total.
 *   - complementario → sale de otro concepto de la hoja con un cálculo (p. ej. «Área del muro × 2»).
 *   - no_dibujado   → el plano no lo dibuja: estimación de quien presupuesta.
 *   - a_mano        → el plano no la da: la escribió quien presupuesta.
 */
import { makeStyles } from '@fluentui/react-components';
import type { GeneradorPublico } from '@supernova/shared';

const useStyles = makeStyles({
  nota: { color: 'var(--text-secondary)', fontSize: '13px', margin: '4px 0' },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', margin: '4px 0 8px' },
  th: {
    textAlign: 'left',
    padding: '6px 8px',
    borderBottom: '1px solid var(--border-subtle)',
    color: 'var(--text-secondary)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  td: { padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' },
  num: { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
});

const fmt = (n: number | null | undefined, dec = 2): string =>
  n === null || n === undefined
    ? ''
    : n.toLocaleString('es-MX', { minimumFractionDigits: dec, maximumFractionDigits: dec });

interface Props {
  g: GeneradorPublico;
  cantidad: number;
  unidad: string | null;
}

export function ComoSeCalculo({ g, cantidad, unidad }: Props): JSX.Element {
  const s = useStyles();
  const u = unidad ?? g.unidad ?? '';
  const cant = `${fmt(cantidad, g.decimales)} ${u}`.trim();

  if (g.origen === 'a_mano') {
    return (
      <p className={s.nota}>
        <b>Cantidad indicada por quien presupuesta.</b> El plano no la da directamente, así que quien elaboró el
        presupuesto la escribió: {cant}.
      </p>
    );
  }

  if (g.origen === 'no_dibujado') {
    return (
      <p className={s.nota}>
        <b>Estimación de quien presupuesta.</b> Este trabajo no aparece dibujado en el plano, así que no se puede medir
        de él. La cantidad ({cant}) es una estimación hecha a partir de las medidas generales del plano.
      </p>
    );
  }

  if (g.origen === 'complementario') {
    return (
      <div>
        <p className={s.nota}>
          <b>Se calcula a partir de otro concepto de esta hoja</b>; no se mide por separado en el plano.
          {g.regla ? ` Criterio: ${g.regla.trim().replace(/[.s]+$/, '')}.` : ''} Resultado: {cant}.
        </p>
        {g.lineas.length > 0 && (
          <ul className={s.nota}>
            {g.lineas.map((l, i) => (
              <li key={i}>
                {l.grupo ? `${l.grupo} · ` : ''}
                {l.localizacion || 'Elementos del plano'}: {fmt(l.parcial, g.decimales)} {u}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div>
      <p className={s.nota}>
        <b>Medido del plano.</b> Cada fila es una parte del plano que suma a la cantidad; largo, ancho y alto van en
        metros.
      </p>
      {g.lineas.length > 0 && (
        <table className={s.tabla}>
          <thead>
            <tr>
              <th className={s.th}>Dónde</th>
              <th className={`${s.th} ${s.num}`}>Piezas</th>
              <th className={`${s.th} ${s.num}`}>Largo</th>
              <th className={`${s.th} ${s.num}`}>Ancho</th>
              <th className={`${s.th} ${s.num}`}>Alto</th>
              <th className={`${s.th} ${s.num}`}>Parcial ({u})</th>
            </tr>
          </thead>
          <tbody>
            {g.lineas.map((l, i) => (
              <tr key={i}>
                <td className={s.td}>
                  {l.grupo ? `${l.grupo} · ` : ''}
                  {l.localizacion || 'Elementos del plano'}
                </td>
                <td className={`${s.td} ${s.num}`}>{fmt(l.piezas, 0)}</td>
                <td className={`${s.td} ${s.num}`}>{fmt(l.largo)}</td>
                <td className={`${s.td} ${s.num}`}>{fmt(l.ancho)}</td>
                <td className={`${s.td} ${s.num}`}>{fmt(l.alto)}</td>
                <td className={`${s.td} ${s.num}`}>{fmt(l.parcial, g.decimales)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className={s.nota}>
        Total: {fmt(g.total, g.decimales)} {u}
      </p>
    </div>
  );
}
